<template>
  <div class="mt-3">
    <h4 class="pb-3">
      Key Metrics
    </h4>

    <DataTable
      v-if="canViewMetrics && requestsTableData.length"
      :value="requestsTableData"
    >
      <Column
        field="domain"
        :header="$t('KeyMetrics.requests')"
      />
      <Column
        field="received"
        :header="$t('KeyMetrics.received')"
      />
      <Column
        field="accepted"
        :header="$t('KeyMetrics.accepted')"
      />
      <Column
        field="cancelled"
        :header="$t('KeyMetrics.cancelled')"
      />
      <Column
        field="open"
        :header="$t('KeyMetrics.open')"
      />

      <Column
        field="acceptedWithoutRP"
        :header="$t('KeyMetrics.acceptedWithoutRP')"
      />
    </DataTable>

    <div class="pt-5">
      <ProgressSpinner
        v-if="allRecordsLoaded"
        class="d-flex justify-content-center align-items-center"
      />
      <DataTable
        v-else
        :value="allCounts"
      >
        <Column
          field="processType"
          :header="$t('KeyMetrics.process')"
        />
        <Column
          field="active"
          :header="$t('KeyMetrics.active')"
        />
        <Column
          field="completed"
          :header="$t('KeyMetrics.completed')"
        />
        <Column
          field="cancelled"
          :header="$t('KeyMetrics.cancelled1')"
        />
        <Column
          field="onhold"
          :header="$t('KeyMetrics.onHold')"
        />
        <Column
          field="total"
          :header="$t('KeyMetrics.totalStarted')"
        />
      </DataTable>
      <DataTable
        v-if="allCounts.length === processTypes.length && totalsPerProcessStatus.length"
        :value="totalsPerProcessStatus"
        class="totals"
      >
        <Column
          field="label"
          header="Processen"
        />
        <Column
          field="active"
          header="Actief"
        />
        <Column
          field="completed"
          header="Afgerond"
        />
        <Column
          field="cancelled"
          header="Geannuleerd"
        />
        <Column
          field="onhold"
          header="On Hold"
        />
        <Column
          field="total"
          header="Totaal gestart"
        />
      </DataTable>
    </div>
  </div>
</template>

<script>
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import ProgressSpinner  from 'primevue/progressspinner'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'KeyMetrics',
  components: {
    DataTable,
    Column,
    ProgressSpinner
  },
  data() {
    return {
      allCounts: [],
      tableKey: 0
    }
  },
  computed: {
    ...mapGetters('tenant', ['getActiveProcessTypes']),
    ...mapGetters('requests', [
      'requestsByStatus', 'getRequestCounts'
    ]),
    ...mapGetters('realisations', [
      'records',
    ]),
    ...mapGetters('relations', [
      'realisationUuidByRequestUuid',
    ]),
    ...mapGetters('reports', [
      'getTotals',
    ]),

    processTypes () {
      return this.getActiveProcessTypes.map((type, index) => {
        return {
          index, value: type.value, label: this.$t(`KeyMetrics.${type.value}`)
        }
      })
    },

    canViewMetrics () {
      return !['engie'].includes(process.env.VUE_APP_TENANT)
    },

    allRequestsCount() {
      return this.getRequestCounts?.total || 0
    },
    openRequestCount() {
      return this.requestsByStatus({
        status: 2, // open status
      }).length || 0
    },
    cancelledRequestCount() {
      return this.requestsByStatus({
        status: 4, // open status
      }).length || 0
    },
    acceptedRequests() {
      return this.requestsByStatus({
        status: 3, // accepted
      }).length || 0
    },
    acceptedRequestsWithoutRP() {
      return this.requestsByStatus({
        status: 3, // accepted
      }).filter(request => !request.connectedProcess).length
    },

    allRecordsLoaded () {
      return (this.getRequestCounts.current !== this.getRequestCounts.total) &&
             (this.allCounts.length < this.processTypes.length)
    },

    requestsTableData() {
      return [{
        domain: this.$t('KeyMetrics.public'),
        received: this.allRequestsCount,
        accepted: this.acceptedRequests,
        cancelled: this.cancelledRequestCount,
        open: this.openRequestCount,
        acceptedWithoutRP: this.acceptedRequestsWithoutRP,
      }]
    },

    totalsPerProcessStatus(){
      const totals = {
        label: this.$t('KeyMetrics.totals'),
        onhold: 0,
        completed: 0,
        cancelled: 0,
        active: 0,
        total: 0
    };

    this.allCounts.forEach(item => {
        totals.onhold += item.onhold;
        totals.completed += item.completed;
        totals.cancelled += item.cancelled;
        totals.active += item.active;
        totals.total += item.total;
    });

    return [totals];
    }
  },
  async created () {
    const token = await this.$auth.getTokenSilently();
    await this.loadRequests({ token })
  },
  mounted() {
    if (this.getTotals?.length) {
      this.allCounts = this.getTotals
      return
    }
    this.processTypes.forEach(async (processType) => await this.getProcesses({ processType }))
    this.$store.dispatch('reports/setTotals', this.allCounts)
  },

  methods: {
    ...mapActions('requests', ['loadRequests']),
    fetchProcessesCountByProcessType: async ({ token, processType }) => {
      const api = await fetch('/api/reporting', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          processType
        })
      });

      if (!api.ok) {
        throw new Error('Failed to load');
      }

      const response = await api.json();

      return response
    },

    async getProcesses({ processType }) {
      const token = await this.$auth.getTokenSilently();
      const response = await this.fetchProcessesCountByProcessType({ token, processType: processType.value });
      const statusCount = {
        index: processType.index,
        processType: processType.label,
        onhold: 0,
        completed: 0,
        cancelled: 0,
        active: 0,
        total: response.data.length
      }
      for (let i = 0; i < response.data.length; i++) {
        if (response.data[i].status.completed) {
          statusCount.completed += 1
        } else if (response.data[i].status.cancelled) {
          statusCount.cancelled += 1
        } else if (response.data[i].status.onhold) {
          statusCount.onhold += 1
        } else {
          statusCount.active += 1
        }
      }

      this.allCounts.push(statusCount)
      this.allCounts.sort((a, b) => a.index - b.index)
    }
  }
}
</script>

<style scoped lang="scss">
.totals {
  ::v-deep(thead th) {
    opacity:0;
    padding:0!important;
    height:0;
    max-height:0;
    line-height:0;
  }
  font-weight: bold;
}
::v-deep(.p-datatable .p-datatable-tbody > tr > td),
::v-deep(.p-datatable .p-datatable-thead > tr > th) {
  text-align: right;
}
::v-deep(.p-datatable .p-column-header-content) {
  justify-content: flex-end;
}

::v-deep(.p-datatable .p-datatable-tbody > tr > td:first-child),
::v-deep(.p-datatable .p-datatable-thead > tr > th:first-child .p-column-header-content) {
  text-align: left;
  justify-content: flex-start;
}

</style>
